import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, Link2, Search, Filter, Trash2,
  Plus, RefreshCw, X, ExternalLink, CheckCircle, Clock,
  Loader2, Image, Download, Eye, MessageSquare,
  Smartphone, Key, Hash, Settings, Calendar, Zap, History, Check
} from 'lucide-react';
import { AdminSidebar, AdminSidebarToggle } from '../components/admin/AdminSidebar';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return '/placeholder.png';
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${API_URL}${imagePath}`;
  return `${API_URL}/api/uploads/${imagePath}`;
};

// ============================================================================
// AUTH SECTION - Telegram Authentication
// ============================================================================
function TelegramAuthSection({ authStatus, onAuthStatusChange }) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneCodeHash, setPhoneCodeHash] = useState('');
  const [password2FA, setPassword2FA] = useState('');
  const [step, setStep] = useState('idle'); // idle, code_sent, needs_2fa
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('admin_token');

  const handleSendCode = async () => {
    if (!phone.trim()) {
      toast.error('Veuillez entrer votre numéro de téléphone');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/auth/send-code?phone=${encodeURIComponent(phone)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        setPhoneCodeHash(data.phone_code_hash);
        setStep('code_sent');
        toast.success('Code envoyé ! Vérifiez vos messages Telegram.');
      } else {
        toast.error(data.detail || 'Erreur lors de l\'envoi du code');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      toast.error('Veuillez entrer le code reçu');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        phone,
        code,
        phone_code_hash: phoneCodeHash
      });
      
      const res = await fetch(`${API_URL}/api/telegram/auth/verify-code?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Authentification réussie !');
        setStep('idle');
        onAuthStatusChange();
      } else if (data.needs_2fa) {
        setStep('needs_2fa');
        toast.info('Authentification 2FA requise');
      } else {
        toast.error(data.detail || 'Code invalide');
      }
    } catch (err) {
      toast.error('Erreur de vérification');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!password2FA.trim()) {
      toast.error('Veuillez entrer votre mot de passe 2FA');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/auth/verify-2fa?password=${encodeURIComponent(password2FA)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Authentification 2FA réussie !');
        setStep('idle');
        onAuthStatusChange();
      } else {
        toast.error(data.detail || 'Mot de passe invalide');
      }
    } catch (err) {
      toast.error('Erreur de vérification 2FA');
    } finally {
      setLoading(false);
    }
  };

  if (authStatus?.authorized) {
    return (
      <Card className="border-green-500/30 bg-green-500/5">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Telegram Connecté</CardTitle>
              <CardDescription>
                {authStatus.user?.username ? `@${authStatus.user.username}` : authStatus.user?.phone}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
            <Send className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <CardTitle className="text-lg">Connexion Telegram</CardTitle>
            <CardDescription>
              Connectez-vous pour scraper les chaînes
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === 'idle' && (
          <>
            <div className="space-y-2">
              <Label>Numéro de téléphone (format international)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="+33612345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleSendCode} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer le code'}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'code_sent' && (
          <>
            <div className="p-3 bg-blue-500/10 rounded-lg text-sm text-blue-300">
              Un code a été envoyé sur Telegram à <strong>{phone}</strong>
            </div>
            <div className="space-y-2">
              <Label>Code de vérification</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="12345"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1"
                  maxLength={6}
                />
                <Button onClick={handleVerifyCode} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vérifier'}
                </Button>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setStep('idle')}>
              Changer de numéro
            </Button>
          </>
        )}

        {step === 'needs_2fa' && (
          <>
            <div className="p-3 bg-amber-500/10 rounded-lg text-sm text-amber-300">
              Votre compte a l'authentification 2FA activée
            </div>
            <div className="space-y-2">
              <Label>Mot de passe 2FA</Label>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="Votre mot de passe"
                  value={password2FA}
                  onChange={(e) => setPassword2FA(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleVerify2FA} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// CHANNELS SECTION - Manage Telegram Channels
// ============================================================================
function ChannelsSection({ channels, onChannelsChange }) {
  const [newChannel, setNewChannel] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [adding, setAdding] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const token = localStorage.getItem('admin_token');

  const handleAddChannel = async () => {
    if (!newChannel.trim()) {
      toast.error('Veuillez entrer un nom de chaîne');
      return;
    }
    
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/channels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: newChannel,
          name: newChannelName || newChannel
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Chaîne ajoutée !');
        setNewChannel('');
        setNewChannelName('');
        setShowAddDialog(false);
        onChannelsChange();
      } else {
        toast.error(data.detail || 'Erreur lors de l\'ajout');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Supprimer cette chaîne ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/telegram/channels/${channelId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Chaîne supprimée');
        onChannelsChange();
      }
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const handleToggleChannel = async (channelId, isActive) => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/channels/${channelId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !isActive })
      });
      
      if (res.ok) {
        onChannelsChange();
      }
    } catch (err) {
      toast.error('Erreur de mise à jour');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Hash className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Chaînes Telegram</CardTitle>
              <CardDescription>
                {channels.length} chaîne{channels.length !== 1 ? 's' : ''} configurée{channels.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
          </div>
          <Button onClick={() => setShowAddDialog(true)} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {channels.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Hash className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucune chaîne configurée</p>
            <p className="text-sm">Ajoutez une chaîne Telegram pour commencer</p>
          </div>
        ) : (
          <div className="space-y-3">
            {channels.map((channel) => (
              <div
                key={channel._id}
                className="flex items-center justify-between p-3 rounded-lg bg-card/50 border"
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center",
                    channel.is_active ? "bg-green-500/20" : "bg-gray-500/20"
                  )}>
                    <Send className={cn(
                      "w-4 h-4",
                      channel.is_active ? "text-green-500" : "text-gray-500"
                    )} />
                  </div>
                  <div>
                    <div className="font-medium">{channel.name}</div>
                    <div className="text-sm text-muted-foreground">@{channel.username}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {channel.message_count > 0 && (
                    <Badge variant="secondary">
                      {channel.message_count} msg
                    </Badge>
                  )}
                  <Switch
                    checked={channel.is_active}
                    onCheckedChange={() => handleToggleChannel(channel._id, channel.is_active)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteChannel(channel._id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add Channel Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une chaîne Telegram</DialogTitle>
            <DialogDescription>
              Entrez le nom d'utilisateur de la chaîne (avec ou sans @)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom d'utilisateur de la chaîne</Label>
              <Input
                placeholder="@acbuy_finds ou acbuy_finds"
                value={newChannel}
                onChange={(e) => setNewChannel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Nom d'affichage (optionnel)</Label>
              <Input
                placeholder="ACBuy Finds"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddChannel} disabled={adding}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================================
// SCHEDULING SECTION - Configure Auto Scraping
// ============================================================================
function SchedulingSection({ config, onConfigChange, channels }) {
  const [enabled, setEnabled] = useState(config?.enabled || false);
  const [hour, setHour] = useState(config?.hour || 8);
  const [frequency, setFrequency] = useState(config?.frequency || 'daily');
  const [selectedChannels, setSelectedChannels] = useState(config?.channel_ids || []);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    setEnabled(config?.enabled || false);
    setHour(config?.hour || 8);
    setFrequency(config?.frequency || 'daily');
    setSelectedChannels(config?.channel_ids || []);
  }, [config]);

  // Charger l'historique
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/scrape-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (showHistory) {
      fetchHistory();
    }
  }, [showHistory]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          enabled, 
          hour, 
          frequency,
          channel_ids: selectedChannels.length > 0 ? selectedChannels : null
        })
      });
      
      if (res.ok) {
        toast.success('Configuration sauvegardée');
        onConfigChange();
      } else {
        toast.error('Erreur de sauvegarde');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const selectAllChannels = () => {
    setSelectedChannels(channels.map(c => c._id || c.id));
  };

  const deselectAllChannels = () => {
    setSelectedChannels([]);
  };

  const toggleChannel = (channelId) => {
    setSelectedChannels(prev => 
      prev.includes(channelId) 
        ? prev.filter(id => id !== channelId)
        : [...prev, channelId]
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <CardTitle className="text-lg">Programmation</CardTitle>
            <CardDescription>
              Scraping automatique des chaînes
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Activer le scraping automatique</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Heure d'exécution</Label>
            <Select value={String(hour)} onValueChange={(v) => setHour(parseInt(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {String(i).padStart(2, '0')}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Fréquence</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Tous les jours</SelectItem>
                <SelectItem value="every_2_days">Tous les 2 jours</SelectItem>
                <SelectItem value="weekly">Hebdomadaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Sélection des chaînes */}
        {channels && channels.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Chaînes à scraper</Label>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={selectAllChannels}
                  className="text-xs h-7"
                >
                  Toutes
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={deselectAllChannels}
                  className="text-xs h-7"
                >
                  Aucune
                </Button>
              </div>
            </div>
            <div className="max-h-[150px] overflow-y-auto border border-zinc-700 rounded-lg p-2 space-y-1">
              {channels.map(channel => {
                const channelId = channel._id || channel.id;
                return (
                <div 
                  key={channelId}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    selectedChannels.includes(channelId) 
                      ? 'bg-amber-500/20 border border-amber-500/30' 
                      : 'hover:bg-zinc-800'
                  }`}
                  onClick={() => toggleChannel(channelId)}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                    selectedChannels.includes(channelId) 
                      ? 'bg-amber-500 border-amber-500' 
                      : 'border-zinc-600'
                  }`}>
                    {selectedChannels.includes(channelId) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="text-sm">{channel.name || channel.username}</span>
                </div>
              )})}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedChannels.length === 0 
                ? 'Toutes les chaînes seront scrapées' 
                : `${selectedChannels.length} chaîne(s) sélectionnée(s)`}
            </p>
          </div>
        )}

        {config?.last_run && (
          <div className="text-sm text-muted-foreground">
            Dernier scraping: {new Date(config.last_run).toLocaleString('fr-FR')}
            {config.last_run_count !== undefined && (
              <span className="ml-2">({config.last_run_count} messages)</span>
            )}
          </div>
        )}

        {enabled && config?.next_run && (
          <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
            <div className="flex items-center gap-2 text-green-400">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Prochain scraping programmé</span>
            </div>
            <div className="text-sm text-green-300 mt-1">
              {new Date(config.next_run).toLocaleString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Settings className="w-4 h-4 mr-2" />}
          Sauvegarder la configuration
        </Button>

        {/* Historique */}
        <div className="border-t border-zinc-700 pt-4 mt-4">
          <Button 
            variant="outline" 
            onClick={() => setShowHistory(!showHistory)}
            className="w-full"
          >
            <History className="w-4 h-4 mr-2" />
            {showHistory ? 'Masquer' : 'Voir'} l'historique des exécutions
          </Button>
          
          {showHistory && (
            <div className="mt-4 space-y-2">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">Aucun historique disponible</p>
              ) : (
                <div className="max-h-[250px] overflow-y-auto space-y-2">
                  {history.map((entry, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        entry.status === 'success' 
                          ? 'bg-green-500/10 border-green-500/20' 
                          : entry.status === 'partial'
                          ? 'bg-yellow-500/10 border-yellow-500/20'
                          : 'bg-red-500/10 border-red-500/20'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {new Date(entry.date).toLocaleString('fr-FR')}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          entry.status === 'success' ? 'bg-green-500/20 text-green-400' :
                          entry.status === 'partial' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {entry.status === 'success' ? 'Succès' : entry.status === 'partial' ? 'Partiel' : 'Erreur'}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {entry.messages_count || 0} messages • {entry.channels_count || 0} chaîne(s)
                        {entry.triggered_by && (
                          <span className="ml-2">• {entry.triggered_by === 'scheduler' ? '⏰ Auto' : '👤 Manuel'}</span>
                        )}
                      </div>
                      {entry.error && (
                        <p className="text-xs text-red-400 mt-1">{entry.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// IMPORT HISTORY SECTION
// ============================================================================
function ImportHistorySection() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const token = localStorage.getItem('admin_token');

  // Charger les jobs récents
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/telegram/import-jobs-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setJobs(data.jobs || []);
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [token]);

  // Charger le rapport
  const loadReport = async (jobId) => {
    setLoadingReport(true);
    setSelectedJobId(jobId);
    try {
      const res = await fetch(`${API_URL}/api/telegram/import-job/${jobId}/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReportData(data);
        setShowReport(true);
      } else {
        toast.error('Erreur de chargement du rapport');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoadingReport(false);
    }
  };

  // Réessayer les erreurs
  const handleRetryErrors = async () => {
    if (!reportData?.job_id) return;
    setRetrying(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/import-job/${reportData.job_id}/retry-errors`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setShowReport(false);
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <History className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Historique des imports</CardTitle>
              <CardDescription>
                Rapports des dernières importations
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Aucun import récent</p>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {jobs.map((job) => (
                <div 
                  key={job.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-zinc-800/50 ${
                    job.status === 'completed' 
                      ? job.errors > 0 ? 'border-amber-500/30 bg-amber-500/5' : 'border-emerald-500/30 bg-emerald-500/5'
                      : job.status === 'running' ? 'border-blue-500/30 bg-blue-500/5' : 'border-zinc-700'
                  }`}
                  onClick={() => loadReport(job.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {job.status === 'running' ? (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      ) : job.errors > 0 ? (
                        <div className="w-4 h-4 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <span className="text-xs text-amber-500">!</span>
                        </div>
                      ) : (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      )}
                      <span className="text-sm font-medium">
                        {job.imported}/{job.total} importés
                      </span>
                      {job.duplicates > 0 && (
                        <span className="text-xs text-amber-500">({job.duplicates} doublon{job.duplicates > 1 ? 's' : ''})</span>
                      )}
                      {job.errors > 0 && (
                        <span className="text-xs text-red-500">({job.errors} erreur{job.errors > 1 ? 's' : ''})</span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(job.started_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {job.is_retry && (
                    <div className="text-xs text-blue-400 mt-1">↻ Relance d'erreurs</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Rapport */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Rapport d'import
            </DialogTitle>
          </DialogHeader>

          {loadingReport ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : reportData && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Résumé */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-zinc-800 rounded-lg text-center">
                  <div className="text-2xl font-bold">{reportData.summary?.total || 0}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-500">{reportData.summary?.imported || 0}</div>
                  <div className="text-xs text-muted-foreground">Importés</div>
                </div>
                <div className="p-3 bg-red-500/10 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-500">{reportData.summary?.errors || 0}</div>
                  <div className="text-xs text-muted-foreground">Erreurs</div>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-500">{reportData.summary?.duplicates || 0}</div>
                  <div className="text-xs text-muted-foreground">Doublons</div>
                </div>
              </div>

              {/* Produits importés */}
              {reportData.success_items?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-emerald-500">✓ Produits importés ({reportData.success_items.length})</h4>
                  <div className="max-h-[150px] overflow-y-auto border border-emerald-500/20 rounded-lg divide-y divide-zinc-800">
                    {reportData.success_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 hover:bg-zinc-800/50">
                        <span className="text-sm truncate flex-1">{item.product_name || 'Produit'}</span>
                        {item.product_id && (
                          <Button variant="ghost" size="sm" onClick={() => window.open(`/admin/sourcing/edit/${item.product_id}`, '_blank')}>
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Erreurs */}
              {reportData.error_items?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-red-500">✗ Erreurs ({reportData.error_items.length})</h4>
                  <div className="max-h-[150px] overflow-y-auto border border-red-500/20 rounded-lg divide-y divide-zinc-800">
                    {reportData.error_items.map((item, idx) => (
                      <div key={idx} className="p-2">
                        <p className="text-sm truncate">{item.url || 'URL inconnue'}</p>
                        <p className="text-xs text-red-400">{item.error || 'Erreur inconnue'}</p>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleRetryErrors} disabled={retrying} className="w-full bg-red-600 hover:bg-red-700">
                    {retrying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Réessayer les erreurs
                  </Button>
                </div>
              )}

              {/* Doublons */}
              {reportData.duplicate_items?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-amber-500">⚠ Doublons ignorés ({reportData.duplicate_items.length})</h4>
                  <p className="text-xs text-muted-foreground">Ces produits existaient déjà dans la base</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReport(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// IMAGE ZOOM MODAL
// ============================================================================
function ImageZoomModal({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);

  if (!src) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      <div className="absolute top-4 right-4 flex gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={(e) => { e.stopPropagation(); setScale(s => Math.max(0.5, s - 0.25)); }}
        >
          -
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={(e) => { e.stopPropagation(); setScale(s => Math.min(3, s + 0.25)); }}
        >
          +
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <img
        src={src}
        alt={alt}
        className="max-w-[90vw] max-h-[90vh] object-contain transition-transform duration-200"
        style={{ transform: `scale(${scale})` }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ============================================================================
// SCRAPING CONTROL SECTION - Period Selection, Preview, Progress
// ============================================================================
function ScrapingControlSection({ onScrapeComplete }) {
  const [selectedPeriod, setSelectedPeriod] = useState('yesterday');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const token = localStorage.getItem('admin_token');

  const periods = [
    { value: 'today', label: "Aujourd'hui" },
    { value: 'yesterday', label: 'Hier' },
    { value: '7d', label: '7 derniers jours' },
    { value: '14d', label: '14 derniers jours' },
    { value: '30d', label: '30 derniers jours' },
    { value: 'last_month', label: 'Mois dernier' },
  ];

  // Fetch preview
  const handlePreview = async (period) => {
    setSelectedPeriod(period);
    setPreviewLoading(true);
    setShowPreview(true);
    
    try {
      const res = await fetch(`${API_URL}/api/telegram/scrape/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ period })
      });
      
      const data = await res.json();
      if (res.ok) {
        setPreviewData(data);
      } else {
        toast.error(data.detail || 'Erreur lors du preview');
        setShowPreview(false);
      }
    } catch (err) {
      toast.error('Erreur de connexion');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Start scraping
  const handleStartScrape = async () => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/scrape`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ period: selectedPeriod })
      });
      
      const data = await res.json();
      if (res.ok) {
        setShowPreview(false);
        setPreviewData(null);
        toast.success(data.message);
        // Start polling for status
        startPolling();
      } else {
        toast.error(data.detail || 'Erreur lors du lancement');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  // Poll for status
  const startPolling = () => {
    setIsPolling(true);
    pollStatus();
  };

  const pollStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/scrape/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setScrapeStatus(data);
        
        if (data.status === 'running') {
          // Continue polling
          setTimeout(pollStatus, 2000);
        } else {
          // Scraping finished
          setIsPolling(false);
          if (data.status === 'completed') {
            toast.success(`Scraping terminé: ${data.messages_saved} produits importés`);
            onScrapeComplete();
          }
        }
      }
    } catch (err) {
      console.error('Polling error:', err);
      setIsPolling(false);
    }
  };

  // Cancel scraping
  const handleCancel = async () => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/scrape/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.info('Scraping annulé');
        setIsPolling(false);
        setScrapeStatus(null);
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Check initial status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/telegram/scrape/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'running') {
            setScrapeStatus(data);
            startPolling();
          }
        }
      } catch (err) {
        console.error('Status check error:', err);
      }
    };
    checkStatus();
  }, [token]);

  // Format time
  const formatTime = (seconds) => {
    if (seconds < 60) return `~${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `~${mins}min ${secs > 0 ? `${secs}s` : ''}`;
  };

  return (
    <>
      {/* Scraping Control Card */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Lancer un Scraping</CardTitle>
                <CardDescription>
                  Sélectionnez une période pour récupérer les produits
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Period Buttons */}
          {!isPolling && (
            <div className="flex flex-wrap gap-2">
              {periods.map((p) => (
                <Button
                  key={p.value}
                  variant={selectedPeriod === p.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePreview(p.value)}
                  disabled={previewLoading}
                >
                  {previewLoading && selectedPeriod === p.value ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {p.label}
                </Button>
              ))}
            </div>
          )}

          {/* Progress Panel */}
          {isPolling && scrapeStatus && (
            <div className="space-y-4">
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progression</span>
                  <span>{scrapeStatus.progress || 0}%</span>
                </div>
                <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${scrapeStatus.progress || 0}%` }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-gray-800/50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-400">
                    {scrapeStatus.channels_done || 0}/{scrapeStatus.channels_total || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Chaînes</div>
                </div>
                <div className="p-3 bg-gray-800/50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-400">
                    {scrapeStatus.messages_found || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Trouvés</div>
                </div>
                <div className="p-3 bg-gray-800/50 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-400">
                    {scrapeStatus.messages_saved || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Importés</div>
                </div>
              </div>

              {/* Current Channel */}
              {scrapeStatus.current_channel && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>En cours: {scrapeStatus.current_channel}</span>
                </div>
              )}

              {/* Logs */}
              <div className="bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs">
                {(scrapeStatus.logs || []).map((log, idx) => (
                  <div key={idx} className="text-gray-400 py-0.5">
                    {log}
                  </div>
                ))}
              </div>

              {/* Cancel Button */}
              <Button variant="destructive" onClick={handleCancel} className="w-full">
                <X className="w-4 h-4 mr-2" />
                Annuler le scraping
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Aperçu du scraping
            </DialogTitle>
            <DialogDescription>
              Vérifiez les messages à scraper avant de lancer
            </DialogDescription>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : previewData ? (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                  <div className="text-3xl font-bold text-emerald-400">
                    {previewData.total_messages}
                  </div>
                  <div className="text-sm text-muted-foreground">Messages à scraper</div>
                </div>
                <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                  <div className="text-3xl font-bold text-blue-400">
                    {formatTime(previewData.estimated_time_seconds)}
                  </div>
                  <div className="text-sm text-muted-foreground">Temps estimé</div>
                </div>
              </div>

              {/* Period */}
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <div className="text-sm font-medium">Période: {previewData.period_label}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Du {new Date(previewData.start_date).toLocaleDateString('fr-FR')} au {new Date(previewData.end_date).toLocaleDateString('fr-FR')}
                </div>
              </div>

              {/* Channels breakdown */}
              <div className="space-y-2">
                <div className="text-sm font-medium">Détail par chaîne:</div>
                {previewData.channels.map((ch) => (
                  <div key={ch.id} className="flex justify-between items-center p-2 bg-gray-800/30 rounded">
                    <span className="text-sm">@{ch.username}</span>
                    <Badge variant={ch.message_count > 0 ? 'default' : 'secondary'}>
                      {ch.message_count} msg
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleStartScrape} 
              disabled={!previewData || previewData.total_messages === 0}
            >
              <Zap className="w-4 h-4 mr-2" />
              Lancer le scraping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// SCRAPED ITEMS SECTION - Product List
// ============================================================================
function ScrapedItemsSection({ items, pendingCount, onItemsChange }) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [importing, setImporting] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, imported: 0, errors: 0 });
  const [lastCompletedJobId, setLastCompletedJobId] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const token = localStorage.getItem('admin_token');

  // Reprendre un import en cours au chargement de la page
  useEffect(() => {
    const savedJobId = localStorage.getItem('telegram_import_job_id');
    if (savedJobId) {
      // Vérifier si le job est toujours en cours
      const checkJob = async () => {
        try {
          const res = await fetch(`${API_URL}/api/telegram/import-job/${savedJobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'in_progress' || data.status === 'pending') {
              // Reprendre le polling
              setBulkImporting(true);
              setImportProgress({
                current: data.processed || 0,
                total: data.total || 0,
                imported: data.imported || 0,
                errors: data.errors || 0
              });
              startPolling(savedJobId);
            } else {
              // Job terminé, nettoyer
              localStorage.removeItem('telegram_import_job_id');
              if (data.status === 'completed') {
                setLastCompletedJobId(savedJobId);
              }
            }
          }
        } catch (err) {
          localStorage.removeItem('telegram_import_job_id');
        }
      };
      checkJob();
    }
  }, [token]);

  // Fonction de polling réutilisable
  const startPolling = (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await fetch(`${API_URL}/api/telegram/import-job/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const statusData = await statusRes.json();
        
        setImportProgress({
          current: statusData.processed || 0,
          total: statusData.total || 0,
          imported: statusData.imported || 0,
          errors: statusData.errors || 0
        });
        
        if (statusData.status === 'completed') {
          clearInterval(pollInterval);
          setBulkImporting(false);
          setSelectedItems([]);
          setLastCompletedJobId(jobId);
          localStorage.removeItem('telegram_import_job_id');
          onItemsChange();
          
          if (statusData.imported > 0) {
            toast.success(`${statusData.imported} produit${statusData.imported > 1 ? 's' : ''} importé${statusData.imported > 1 ? 's' : ''} !`);
          }
          // Afficher les doublons séparément des erreurs
          if (statusData.duplicates > 0) {
            toast.warning(
              <div className="flex flex-col gap-1">
                <span className="font-medium">⚠️ {statusData.duplicates} doublon{statusData.duplicates > 1 ? 's' : ''}</span>
                <span className="text-xs opacity-80">Produit{statusData.duplicates > 1 ? 's' : ''} déjà existant{statusData.duplicates > 1 ? 's' : ''} dans la base</span>
              </div>,
              { duration: 5000 }
            );
          }
          if (statusData.errors > 0) {
            toast.error(
              <div className="flex flex-col gap-1">
                <span className="font-medium">❌ {statusData.errors} erreur{statusData.errors > 1 ? 's' : ''} technique{statusData.errors > 1 ? 's' : ''}</span>
                <span className="text-xs opacity-80">Cliquez sur le bouton rouge en bas à droite pour voir le détail</span>
              </div>,
              { duration: 8000 }
            );
          }
        }
      } catch (pollErr) {
        console.error('Error polling import status:', pollErr);
      }
    }, 2000);
    
    // Retourner l'interval pour pouvoir le nettoyer si nécessaire
    return pollInterval;
  };

  // Select all handler
  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(i => i._id));
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleImport = async (itemId) => {
    setImporting(itemId);
    try {
      const res = await fetch(`${API_URL}/api/telegram/scraped/${itemId}/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Produit importé !');
        onItemsChange();
      } else {
        toast.error(data.detail || 'Erreur d\'import');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setImporting(null);
    }
  };

  const handleIgnore = async (itemId) => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/scraped/${itemId}/ignore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Produit ignoré');
        onItemsChange();
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Charger le rapport d'import
  const loadReport = async (jobId) => {
    setLoadingReport(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/import-job/${jobId}/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setReportData(data);
        setShowReport(true);
      } else {
        toast.error('Erreur de chargement du rapport');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoadingReport(false);
    }
  };

  // Réessayer les erreurs
  const handleRetryErrors = async () => {
    if (!reportData?.job_id) return;
    
    setRetrying(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/import-job/${reportData.job_id}/retry-errors`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setShowReport(false);
        setLastCompletedJobId(data.new_job_id);
        onItemsChange();
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setRetrying(false);
    }
  };

  const handleBulkImport = async () => {
    if (selectedItems.length === 0) return;
    
    setBulkImporting(true);
    setImportProgress({ current: 0, total: selectedItems.length, imported: 0, errors: 0 });
    
    try {
      // Lancer l'import en arrière-plan
      const res = await fetch(`${API_URL}/api/telegram/scraped/import-bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedItems)
      });
      
      const data = await res.json();
      
      if (data.job_id) {
        // Sauvegarder le job_id pour reprendre si on quitte la page
        localStorage.setItem('telegram_import_job_id', data.job_id);
        toast.info('Import lancé en arrière-plan. Vous pouvez quitter cette page.');
        
        // Utiliser la fonction de polling réutilisable
        startPolling(data.job_id);
      }
    } catch (err) {
      setBulkImporting(false);
      localStorage.removeItem('telegram_import_job_id');
      toast.error('Erreur de lancement de l\'import');
    }
  };

  const handleBulkIgnore = async () => {
    if (selectedItems.length === 0) return;
    
    try {
      const res = await fetch(`${API_URL}/api/telegram/scraped/ignore-bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedItems)
      });
      
      if (res.ok) {
        toast.success('Produits ignorés');
        setSelectedItems([]);
        onItemsChange();
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  return (
    <Card className="col-span-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <Image className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Produits Scrapés</CardTitle>
              <CardDescription>
                {pendingCount} produit{pendingCount !== 1 ? 's' : ''} en attente d'import
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {items.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedItems.length === items.length ? 'Désélectionner tout' : 'Sélectionner tout'}
              </Button>
            )}
            <Select value={filter} onValueChange={(v) => { setFilter(v); onItemsChange(v); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="imported">Importés</SelectItem>
                <SelectItem value="ignored">Ignorés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Aucun produit {filter === 'pending' ? 'en attente' : filter === 'imported' ? 'importé' : 'ignoré'}</p>
            <p className="text-sm mt-2">Lancez un scraping pour récupérer des produits</p>
          </div>
        ) : (
          <>
            {/* Bulk Actions */}
            {selectedItems.length > 0 && (
              <div className="mb-4 p-3 bg-blue-500/10 rounded-lg flex items-center justify-between">
                <span>{selectedItems.length} sélectionné{selectedItems.length > 1 ? 's' : ''}</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleBulkImport}>
                    <Download className="w-4 h-4 mr-2" />
                    Importer
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleBulkIgnore}>
                    <X className="w-4 h-4 mr-2" />
                    Ignorer
                  </Button>
                </div>
              </div>
            )}

            {/* Items Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((item) => (
                <div
                  key={item._id}
                  className={cn(
                    "relative group rounded-lg border overflow-hidden transition-all bg-gray-900/50",
                    selectedItems.includes(item._id) && "ring-2 ring-blue-500",
                    item.status === 'imported' && "opacity-60"
                  )}
                >
                  {/* Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <Checkbox
                      checked={selectedItems.includes(item._id)}
                      onCheckedChange={() => handleSelectItem(item._id)}
                      className="bg-black/50 border-white/50"
                    />
                  </div>

                  {/* Image */}
                  <div 
                    className="aspect-square bg-gray-800 cursor-zoom-in relative overflow-hidden"
                    onClick={() => item.image_path && setZoomedImage(getImageUrl(item.image_path))}
                  >
                    {item.image_path ? (
                      <img
                        src={getImageUrl(item.image_path)}
                        alt={item.title || "Produit Telegram"}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/placeholder.png';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-12 h-12 opacity-30" />
                      </div>
                    )}
                    
                    {/* Zoom indicator */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3 space-y-2">
                    {/* Title */}
                    <div className="font-medium text-sm line-clamp-2 min-h-[2.5rem]">
                      {item.title || <span className="text-gray-500 italic">Sans titre</span>}
                    </div>

                    {/* Price - Always show section */}
                    <div className="space-y-1 min-h-[3rem]">
                      {item.price_usd ? (
                        <>
                          <div className="text-lg font-bold text-green-400">
                            ${item.price_usd.toFixed(2)} USD
                          </div>
                          {item.price_eur && (
                            <div className="text-xs text-muted-foreground">
                              ({item.price_eur.toFixed(2)} €)
                            </div>
                          )}
                        </>
                      ) : item.price_eur ? (
                        <>
                          <div className="text-lg font-bold text-green-400">
                            {item.price_eur.toFixed(2)} €
                          </div>
                        </>
                      ) : item.price_original ? (
                        <div className="text-lg font-medium text-amber-400">
                          {item.price_original}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 italic">Prix non détecté</div>
                      )}
                    </div>

                    {/* Agent Link */}
                    <div className="min-h-[1.5rem]">
                      {item.agent_link ? (
                        <a
                          href={item.agent_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate max-w-[150px]">{item.agent_name || 'Lien produit'}</span>
                        </a>
                      ) : (
                        <span className="text-xs text-gray-500 italic">Pas de lien</span>
                      )}
                    </div>

                    {/* Channel & Date */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-gray-700">
                      <span>@{item.channel_username}</span>
                      <span>{new Date(item.message_date).toLocaleDateString('fr-FR')}</span>
                    </div>

                    {/* Actions */}
                    {item.status === 'pending' && (
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => handleImport(item._id)}
                          disabled={importing === item._id}
                        >
                          {importing === item._id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              Importer
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleIgnore(item._id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {item.status === 'imported' && (
                      <Badge className="bg-green-500/20 text-green-400 w-full justify-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Importé
                      </Badge>
                    )}

                    {item.status === 'ignored' && (
                      <Badge className="bg-gray-500/20 text-gray-400 w-full justify-center">
                        Ignoré
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>

      {/* Bulk Import Progress Modal */}
      <Dialog open={bulkImporting} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              Import en cours...
            </DialogTitle>
            <DialogDescription>
              Chaque produit est enrichi avec les données ACBuy complètes (images HD, variantes, tags AI)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progression</span>
                <span className="font-medium">{importProgress.current} / {importProgress.total}</span>
              </div>
              <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                <div className="text-2xl font-bold text-emerald-400">{importProgress.imported}</div>
                <div className="text-xs text-muted-foreground">Importés</div>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-400">{importProgress.errors}</div>
                <div className="text-xs text-muted-foreground">Erreurs</div>
              </div>
            </div>

            {/* Info */}
            <div className="p-3 bg-amber-500/10 rounded-lg text-sm text-amber-300">
              <Clock className="w-4 h-4 inline mr-2" />
              ~30 secondes par produit (scraping + IA)
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bouton Voir Rapport - Plus visible avec erreurs techniques */}
      {lastCompletedJobId && !bulkImporting && (
        <div className={`fixed bottom-4 right-4 z-50 ${importProgress.errors > 0 ? 'animate-bounce' : ''}`}>
          <Button
            onClick={() => loadReport(lastCompletedJobId)}
            disabled={loadingReport}
            className={`shadow-lg ${
              importProgress.errors > 0 
                ? 'bg-red-600 hover:bg-red-700 ring-2 ring-red-400 ring-offset-2 ring-offset-black' 
                : importProgress.duplicates > 0
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-blue-600 hover:bg-blue-700'
            }`}
            size="lg"
          >
            {loadingReport ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Eye className="w-5 h-5 mr-2" />}
            {importProgress.errors > 0 
              ? `❌ ${importProgress.errors} erreur${importProgress.errors > 1 ? 's' : ''} - Voir rapport`
              : importProgress.duplicates > 0
                ? `⚠️ ${importProgress.duplicates} doublon${importProgress.duplicates > 1 ? 's' : ''} - Voir rapport`
                : 'Voir le rapport d\'import'
            }
          </Button>
        </div>
      )}

      {/* Modal Rapport d'Import */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Rapport d'import
            </DialogTitle>
            <DialogDescription>
              Détail de l'importation des produits Telegram
            </DialogDescription>
          </DialogHeader>

          {reportData && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Résumé */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-gray-100 dark:bg-zinc-800 rounded-lg text-center">
                  <div className="text-2xl font-bold">{reportData.summary?.total || 0}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-500">{reportData.summary?.imported || 0}</div>
                  <div className="text-xs text-muted-foreground">Importés</div>
                </div>
                <div className="p-3 bg-red-500/10 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-500">{reportData.summary?.errors || 0}</div>
                  <div className="text-xs text-muted-foreground">Erreurs</div>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-500">{reportData.summary?.duplicates || 0}</div>
                  <div className="text-xs text-muted-foreground">Doublons</div>
                </div>
              </div>

              {/* Durée */}
              {reportData.duration_seconds && (
                <div className="text-sm text-muted-foreground">
                  Durée totale: {Math.round(reportData.duration_seconds / 60)} minutes
                </div>
              )}

              {/* Produits importés avec succès */}
              {reportData.success_items?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-emerald-500 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Produits importés ({reportData.success_items.length})
                  </h4>
                  <div className="max-h-[200px] overflow-y-auto border border-emerald-500/20 rounded-lg">
                    {reportData.success_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.product_name || 'Produit importé'}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.url?.substring(0, 50)}...</p>
                        </div>
                        {item.product_slug && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/admin/sourcing/edit/${item.product_id}`, '_blank')}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Erreurs */}
              {reportData.error_items?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-red-500 flex items-center gap-2">
                    <X className="w-4 h-4" />
                    Erreurs ({reportData.error_items.length})
                  </h4>
                  <div className="max-h-[200px] overflow-y-auto border border-red-500/20 rounded-lg">
                    {reportData.error_items.map((item, idx) => (
                      <div key={idx} className="p-2 border-b border-zinc-800 last:border-0">
                        <p className="text-sm truncate">{item.url || 'URL inconnue'}</p>
                        <p className="text-xs text-red-400 mt-1">{item.error || 'Erreur inconnue'}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Bouton réessayer */}
                  <Button
                    onClick={handleRetryErrors}
                    disabled={retrying}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    {retrying ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Relance en cours...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" /> Réessayer les {reportData.error_items.length} erreurs</>
                    )}
                  </Button>
                </div>
              )}

              {/* Doublons */}
              {reportData.duplicate_items?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-amber-500 flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    Doublons ignorés ({reportData.duplicate_items.length})
                  </h4>
                  <div className="max-h-[150px] overflow-y-auto border border-amber-500/20 rounded-lg">
                    {reportData.duplicate_items.map((item, idx) => (
                      <div key={idx} className="p-2 border-b border-zinc-800 last:border-0 text-sm text-muted-foreground truncate">
                        {item.url || 'URL inconnue'}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReport(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <ImageZoomModal
          src={zoomedImage}
          alt="Product"
          onClose={() => setZoomedImage(null)}
        />
      )}
    </Card>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export default function AdminTelegramScraperPage() {
  const navigate = useNavigate();
  const { theme } = useAdminTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const token = localStorage.getItem('admin_token');

  // State
  const [authStatus, setAuthStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch auth status
  const fetchAuthStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/auth/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAuthStatus(data);
      }
    } catch (err) {
      console.error('Error fetching auth status:', err);
    }
  }, [token]);

  // Fetch channels
  const fetchChannels = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setChannels(data.channels || []);
      }
    } catch (err) {
      console.error('Error fetching channels:', err);
    }
  }, [token]);

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, [token]);

  // Fetch scraped items
  const fetchItems = useCallback(async (status = 'pending') => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/scraped?status=${status}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setPendingCount(data.pending_count || 0);
      }
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchAuthStatus(),
        fetchChannels(),
        fetchConfig(),
        fetchItems()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchAuthStatus, fetchChannels, fetchConfig, fetchItems]);

  return (
    <div className={cn(
      "min-h-screen flex",
      theme === 'dark' ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'
    )}>
      <AdminSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className={cn(
        "flex-1 transition-all duration-300",
        sidebarOpen ? "md:ml-64" : "ml-0"
      )}>
        {/* Header */}
        <header className={cn(
          "sticky top-0 z-40 border-b px-6 py-4",
          theme === 'dark' ? 'bg-gray-950/95 border-gray-800' : 'bg-white/95 border-gray-200'
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(!sidebarOpen)} />
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Send className="w-6 h-6 text-blue-500" />
                  Telegram Scraper
                </h1>
                <p className="text-sm text-muted-foreground">
                  Importez automatiquement des produits depuis Telegram
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Auth & Channels */}
              <div className="space-y-6">
                <TelegramAuthSection
                  authStatus={authStatus}
                  onAuthStatusChange={fetchAuthStatus}
                />
                <ChannelsSection
                  channels={channels}
                  onChannelsChange={fetchChannels}
                />
                <SchedulingSection
                  config={config}
                  onConfigChange={fetchConfig}
                  channels={channels}
                />
                <ImportHistorySection />
              </div>

              {/* Right Column - Scraping Control & Items */}
              <div className="lg:col-span-2 space-y-6">
                <ScrapingControlSection
                  onScrapeComplete={fetchItems}
                />
                <ScrapedItemsSection
                  items={items}
                  pendingCount={pendingCount}
                  onItemsChange={fetchItems}
                />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
